import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type CourseCategoryItem = {
  id: number;
  title: string;
  description?: string | null;
  thumbnail?: string | null;
  allowed_departments: string[];
  is_active?: boolean;
};

type UserItem = {
  id: number;
  username: string;
  email: string;
  department: string;
  role: "employee" | "admin";
};

type SubCourseForm = {
  name: string;
  instructor: string;
  status: "published" | "private";
  thumbnail: string;
  video_url: string;
  ppt_url: string;
  ppt_preview_url: string;
 ppt_slide_urls: string;
  pdf_url: string;
  start_date: string;
  end_date: string;
  
};

const emptySubCourseForm: SubCourseForm = {
  name: "",
  instructor: "",
  status: "private",
  thumbnail: "",
  video_url: "",
  ppt_url: "",
  ppt_preview_url: "",
  ppt_slide_urls: "",
  pdf_url: "",
  start_date: "",
  end_date: "",
  
};

function CoursesPage() {
  const navigate = useNavigate();

  const [categories, setCategories] = useState<CourseCategoryItem[]>([]);
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [openMenuCategoryId, setOpenMenuCategoryId] = useState<number | null>(
    null
  );

  const [subCourseCategory, setSubCourseCategory] =
    useState<CourseCategoryItem | null>(null);
  const [subCourseForm, setSubCourseForm] =
    useState<SubCourseForm>(emptySubCourseForm);

  const [editingCategory, setEditingCategory] =
    useState<CourseCategoryItem | null>(null);
  const [editSelectedDepartments, setEditSelectedDepartments] = useState<
    string[]
  >([]);
  const [isEditDepartmentDropdownOpen, setIsEditDepartmentDropdownOpen] =
    useState(false);

  const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] =
    useState(false);

  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);

  const [categoryForm, setCategoryForm] = useState({
    title: "",
    description: "",
    thumbnail: "",
  });

  const fetchCategories = useCallback(async () => {
    const response = await fetch("/course-categories/");

    if (!response.ok) {
      throw new Error("Failed to fetch course categories");
    }

    const data = await response.json();
    setCategories(data);
  }, []);

  const fetchUsers = useCallback(async () => {
    const response = await fetch("/users/");

    if (!response.ok) {
      throw new Error("Failed to fetch users");
    }

    const data = await response.json();
    setUsers(data);
  }, []);

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([fetchCategories(), fetchUsers()]);
    } catch (error) {
      console.error("Error fetching initial data:", error);
    } finally {
      setLoading(false);
    }
  }, [fetchCategories, fetchUsers]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const departmentOptions = useMemo(() => {
    const departments = users
      .map((user) => user.department)
      .filter((department) => department && department.trim() !== "");

    return Array.from(new Set(departments));
  }, [users]);

  const activeCategories = useMemo(() => {
    return categories.filter((category) => category.is_active !== false);
  }, [categories]);

  const handleDepartmentCheckboxChange = (department: string) => {
    setSelectedDepartments((prevDepartments) => {
      if (prevDepartments.includes(department)) {
        return prevDepartments.filter((item) => item !== department);
      }

      return [...prevDepartments, department];
    });
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setIsDepartmentDropdownOpen(false);
    setSelectedDepartments([]);
    setCategoryForm({
      title: "",
      description: "",
      thumbnail: "",
    });
  };

  const handleUploadThumbnail = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch("/courses/upload", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload thumbnail");
      }

      const data = await response.json();

      setCategoryForm((prevForm) => ({
        ...prevForm,
        thumbnail: data.url,
      }));
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
      alert("サムネイル画像のアップロードに失敗しました。");
    }
  };

  const handleUploadSubCourseFile = async (
  file: File,
  fieldName: "thumbnail" | "video_url" | "ppt_url" | "ppt_slide_urls" | "pdf_url"
) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch("/courses/upload", {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Failed to upload file");
    }

    const data = await response.json();

    setSubCourseForm((prevForm) => {
      if (fieldName === "ppt_slide_urls") {
        const existingUrls = prevForm.ppt_slide_urls
          ? prevForm.ppt_slide_urls.split(",").filter(Boolean)
          : [];

        return {
          ...prevForm,
          ppt_slide_urls: [...existingUrls, data.url].join(","),
        };
      }

      if (fieldName === "ppt_url") {
  return {
    ...prevForm,
    ppt_url: data.url,
    ppt_preview_url: data.ppt_preview_url || "",
  };
}

return {
  ...prevForm,
  [fieldName]: data.url,
};
    });
  } catch (error) {
    console.error("Error uploading sub course file:", error);
    alert("ファイルのアップロードに失敗しました。");
  }
};

  const handleCreateCategory = async () => {
    if (!categoryForm.title.trim()) {
      alert("見出しタイトルを入力してください。");
      return;
    }

    if (selectedDepartments.length === 0) {
      alert("対象部署を1つ以上選択してください。");
      return;
    }

    try {
      const payload = {
        title: categoryForm.title,
        description: categoryForm.description,
        thumbnail: categoryForm.thumbnail,
        allowed_departments: selectedDepartments,
      };

      const response = await fetch("/course-categories/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to create course category");
      }

      await fetchCategories();
      alert("メイン見出しを作成しました。");
      handleCloseCreateModal();
    } catch (error) {
      console.error("Error creating course category:", error);
      alert("メイン見出しの作成に失敗しました。");
    }
  };

  const getDepartmentDropdownLabel = () => {
    if (selectedDepartments.length === 0) {
      return "部署を選択してください";
    }

    if (selectedDepartments.length <= 2) {
      return selectedDepartments.join(", ");
    }

    return `${selectedDepartments.length}件選択済み`;
  };

  const getFallbackCategoryThumbnailUrl = (category: CourseCategoryItem) => {
    const text = `${category.title || ""} ${
      category.description || ""
    }`.toLowerCase();

    if (
      text.includes("react") ||
      text.includes("javascript") ||
      text.includes("typescript") ||
      text.includes("programming") ||
      text.includes("code") ||
      text.includes("web") ||
      text.includes("frontend") ||
      text.includes("backend") ||
      text.includes("system") ||
      text.includes("it") ||
      text.includes("システム")
    ) {
      return "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80";
    }

    if (
      text.includes("security") ||
      text.includes("cyber") ||
      text.includes("password") ||
      text.includes("network") ||
      text.includes("privacy") ||
      text.includes("セキュリティ")
    ) {
      return "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=900&q=80";
    }

    if (
      text.includes("business") ||
      text.includes("management") ||
      text.includes("leader") ||
      text.includes("sales") ||
      text.includes("meeting") ||
      text.includes("office") ||
      text.includes("マネジメント") ||
      text.includes("営業") ||
      text.includes("管理")
    ) {
      return "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=900&q=80";
    }

    if (
      text.includes("training") ||
      text.includes("learning") ||
      text.includes("education") ||
      text.includes("lesson") ||
      text.includes("course") ||
      text.includes("研修") ||
      text.includes("教育") ||
      text.includes("学習")
    ) {
      return "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=900&q=80";
    }

    if (
      text.includes("health") ||
      text.includes("medical") ||
      text.includes("clinic") ||
      text.includes("beauty") ||
      text.includes("care") ||
      text.includes("健康") ||
      text.includes("美容") ||
      text.includes("医療")
    ) {
      return "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=900&q=80";
    }

    const fallbackThumbnails = [
      "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=900&q=80",
    ];

    const index = Math.abs(category.id) % fallbackThumbnails.length;
    return fallbackThumbnails[index];
  };

  const getThumbnailUrl = (
    thumbnail?: string | null,
    category?: CourseCategoryItem
  ) => {
    if (!thumbnail) {
      if (category) {
        return getFallbackCategoryThumbnailUrl(category);
      }

      return "";
    }

    if (thumbnail.startsWith("http")) {
      return thumbnail;
    }

    return `${thumbnail}`;
  };

  const handleCategoryClick = (category: CourseCategoryItem) => {
    navigate(
      `/courses/category/${category.id}?title=${encodeURIComponent(
        category.title
      )}`
    );
  };

  const handleCreateSubCourseClick = (category: CourseCategoryItem) => {
    setOpenMenuCategoryId(null);
    setSubCourseCategory(category);
    setSubCourseForm(emptySubCourseForm);
  };

  const handleCloseSubCourseModal = () => {
    setSubCourseCategory(null);
    setSubCourseForm(emptySubCourseForm);
  };

  const handleCreateSubCourse = async () => {
    if (!subCourseCategory) {
      return;
    }

    if (!subCourseForm.name.trim()) {
      alert("コース名を入力してください。");
      return;
    }

    try {
      const payload = {
        category_id: subCourseCategory.id,
        category: subCourseCategory.title,
        name: subCourseForm.name,
        instructor: subCourseForm.instructor,
        department: subCourseCategory.allowed_departments.join(", "),
        assigned_user: "",
        status: subCourseForm.status,
        students: 0,
        thumbnail: subCourseForm.thumbnail,
        video_url: subCourseForm.video_url,
        ppt_url: subCourseForm.ppt_url,
        ppt_preview_url: subCourseForm.ppt_preview_url,
        ppt_slide_urls: subCourseForm.ppt_slide_urls,
        pdf_url: subCourseForm.pdf_url,
        start_date: subCourseForm.start_date,
        end_date: subCourseForm.end_date,
        
      };

      const response = await fetch("/courses/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to create sub course");
      }

      alert("サブコースを作成しました。");
      handleCloseSubCourseModal();
    } catch (error) {
      console.error("Error creating sub course:", error);
      alert("サブコースの作成に失敗しました。");
    }
  };

  const handleEditCategoryClick = (category: CourseCategoryItem) => {
    setOpenMenuCategoryId(null);
    setEditingCategory(category);
    setEditSelectedDepartments(category.allowed_departments || []);
    setIsEditDepartmentDropdownOpen(false);
  };

  const handleCloseEditModal = () => {
    setEditingCategory(null);
    setEditSelectedDepartments([]);
    setIsEditDepartmentDropdownOpen(false);
  };

  const handleEditDepartmentCheckboxChange = (department: string) => {
    setEditSelectedDepartments((prevDepartments) => {
      if (prevDepartments.includes(department)) {
        return prevDepartments.filter((item) => item !== department);
      }

      return [...prevDepartments, department];
    });
  };

  const getEditDepartmentDropdownLabel = () => {
    if (editSelectedDepartments.length === 0) {
      return "部署を選択してください";
    }

    if (editSelectedDepartments.length <= 2) {
      return editSelectedDepartments.join(", ");
    }

    return `${editSelectedDepartments.length}件選択済み`;
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) {
      return;
    }

    if (!editingCategory.title.trim()) {
      alert("見出しタイトルを入力してください。");
      return;
    }

    if (editSelectedDepartments.length === 0) {
      alert("対象部署を1つ以上選択してください。");
      return;
    }

    try {
      const payload = {
        title: editingCategory.title,
        description: editingCategory.description || "",
        thumbnail: editingCategory.thumbnail || "",
        allowed_departments: editSelectedDepartments,
      };

      const response = await fetch(
        `/course-categories/${editingCategory.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update course category");
      }

      await fetchCategories();
      alert("メイン見出しを更新しました。");
      handleCloseEditModal();
    } catch (error) {
      console.error("Error updating course category:", error);
      alert("メイン見出しの更新に失敗しました。");
    }
  };




  const handleDeleteCategory = async (categoryId: number) => {
  const isConfirmed = window.confirm(
    "このメイン見出しを削除すると、中のサブコースも削除されます。削除してもよろしいですか？"
  );

  if (!isConfirmed) return;

  try {
    const response = await fetch(
      `/course-categories/${categoryId}`,
      {
        method: "DELETE",
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete course category");
    }

    setOpenMenuCategoryId(null);
    await fetchCategories();
    alert("メイン見出しとサブコースを削除しました。");
  } catch (error) {
    console.error("Error deleting course category:", error);
    alert("メイン見出しの削除に失敗しました。");
  }
};

  if (loading) {
    return (
      <div style={{ padding: "40px", fontSize: "20px", fontWeight: 600 }}>
        読み込み中...
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {showCreateModal && (
        <div style={modalOverlayStyle}>
          <div style={modalCardStyle}>
            <h2 style={modalTitleStyle}>メイン見出し作成</h2>

            <p style={modalSubtitleStyle}>
              コースをまとめる見出し・対象部署・サムネイルを設定します
            </p>

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>見出しタイトル</label>
              <input
                type="text"
                value={categoryForm.title}
                onChange={(e) =>
                  setCategoryForm({
                    ...categoryForm,
                    title: e.target.value,
                  })
                }
                placeholder="例：美容関連"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>説明</label>
              <textarea
                value={categoryForm.description}
                onChange={(e) =>
                  setCategoryForm({
                    ...categoryForm,
                    description: e.target.value,
                  })
                }
                placeholder="例：美容決済部向けの研修コースをまとめます"
                style={textareaStyle}
              />
            </div>

            <div style={{ marginBottom: "18px", position: "relative" }}>
              <label style={labelStyle}>対象部署選択（複数選択可）</label>

              <button
                type="button"
                onClick={() =>
                  setIsDepartmentDropdownOpen(!isDepartmentDropdownOpen)
                }
                style={dropdownButtonStyle}
              >
                <span>{getDepartmentDropdownLabel()}</span>
                <span>▼</span>
              </button>

              {isDepartmentDropdownOpen && (
                <div style={dropdownPanelStyle}>
                  {departmentOptions.length === 0 && (
                    <div style={emptyTextStyle}>部署がありません</div>
                  )}

                  {departmentOptions.map((department) => (
                    <label key={department} style={checkboxRowStyle}>
                      <input
                        type="checkbox"
                        checked={selectedDepartments.includes(department)}
                        onChange={() =>
                          handleDepartmentCheckboxChange(department)
                        }
                        style={checkboxStyle}
                      />
                      <span>{department}</span>
                    </label>
                  ))}
                </div>
              )}

              <p style={helperTextStyle}>
                この見出しを表示できる部署を選択してください
              </p>
            </div>

            <div style={{ marginBottom: "28px" }}>
              <label style={labelStyle}>サムネイル画像</label>
              <input
                type="text"
                value={categoryForm.thumbnail}
                onChange={(e) =>
                  setCategoryForm({
                    ...categoryForm,
                    thumbnail: e.target.value,
                  })
                }
                placeholder="画像URLを入力、または画像をアップロード"
                style={inputStyle}
              />

              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleUploadThumbnail(file);
                  }
                }}
                style={fileInputStyle}
              />

              {categoryForm.thumbnail && (
                <div style={thumbnailPreviewBoxStyle}>
                  <img
                    src={getThumbnailUrl(categoryForm.thumbnail)}
                    alt="thumbnail preview"
                    style={thumbnailPreviewStyle}
                  />
                  <div style={uploadedFileTextStyle}>アップロード済み</div>
                </div>
              )}
            </div>

            <div style={modalButtonRowStyle}>
              <button onClick={handleCloseCreateModal} style={cancelButtonStyle}>
                キャンセル
              </button>

              <button onClick={handleCreateCategory} style={primaryButtonStyle}>
                作成
              </button>
            </div>
          </div>
        </div>
      )}

      {subCourseCategory && (
        <div style={modalOverlayStyle}>
          <div style={modalCardStyle}>
            <h2 style={modalTitleStyle}>サブコース作成</h2>

            <p style={modalSubtitleStyle}>
              「{subCourseCategory.title}」の中にサブコースを作成します
            </p>

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>コース名</label>
              <input
                type="text"
                value={subCourseForm.name}
                onChange={(e) =>
                  setSubCourseForm({
                    ...subCourseForm,
                    name: e.target.value,
                  })
                }
                placeholder="例：新人研修コース"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>講師名</label>
              <input
                type="text"
                value={subCourseForm.instructor}
                onChange={(e) =>
                  setSubCourseForm({
                    ...subCourseForm,
                    instructor: e.target.value,
                  })
                }
                placeholder="例：山田 太郎"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>開始日</label>
              <input
                type="date"
                value={subCourseForm.start_date}
                onChange={(e) =>
                  setSubCourseForm({
                    ...subCourseForm,
                    start_date: e.target.value,
                  })
                }
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>終了日</label>
              <input
                type="date"
                value={subCourseForm.end_date}
                onChange={(e) =>
                  setSubCourseForm({
                    ...subCourseForm,
                    end_date: e.target.value,
                  })
                }
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>サムネイル画像</label>
              <input
                type="text"
                value={subCourseForm.thumbnail}
                onChange={(e) =>
                  setSubCourseForm({
                    ...subCourseForm,
                    thumbnail: e.target.value,
                  })
                }
                placeholder="画像URLを入力、または画像をアップロード"
                style={inputStyle}
              />

              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleUploadSubCourseFile(file, "thumbnail");
                  }
                }}
                style={fileInputStyle}
              />

              {subCourseForm.thumbnail && (
                <div style={uploadedFileTextStyle}>アップロード済み</div>
              )}
            </div>

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>動画</label>
              <input
                type="text"
                value={subCourseForm.video_url}
                onChange={(e) =>
                  setSubCourseForm({
                    ...subCourseForm,
                    video_url: e.target.value,
                  })
                }
                placeholder="動画URLを入力、または動画をアップロード"
                style={inputStyle}
              />

              <input
                type="file"
                accept="video/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleUploadSubCourseFile(file, "video_url");
                  }
                }}
                style={fileInputStyle}
              />

              {subCourseForm.video_url && (
                <div style={uploadedFileTextStyle}>アップロード済み</div>
              )}
            </div>

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>PPT資料</label>
              <input
                type="text"
                value={subCourseForm.ppt_url}
                onChange={(e) =>
                  setSubCourseForm({
                    ...subCourseForm,
                    ppt_url: e.target.value,
                  })
                }
                placeholder="PPT URLを入力、またはPPTをアップロード"
                style={inputStyle}
              />

              <input
                type="file"
                accept=".ppt,.pptx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleUploadSubCourseFile(file, "ppt_url");
                  }
                }}
                style={fileInputStyle}
              />

              {subCourseForm.ppt_url && (
                <div style={uploadedFileTextStyle}>アップロード済み</div>
              )}
            </div>




          



           







            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>PDF資料</label>
              <input
                type="text"
                value={subCourseForm.pdf_url}
                onChange={(e) =>
                  setSubCourseForm({
                    ...subCourseForm,
                    pdf_url: e.target.value,
                  })
                }
                placeholder="PDF URLを入力、またはPDFをアップロード"
                style={inputStyle}
              />

              <input
                type="file"
                accept=".pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleUploadSubCourseFile(file, "pdf_url");
                  }
                }}
                style={fileInputStyle}
              />

              {subCourseForm.pdf_url && (
                <div style={uploadedFileTextStyle}>アップロード済み</div>
              )}
            </div>

            

            <div style={{ marginBottom: "28px" }}>
              <label style={labelStyle}>ステータス</label>
              <select
                value={subCourseForm.status}
                onChange={(e) =>
                  setSubCourseForm({
                    ...subCourseForm,
                    status: e.target.value as "published" | "private",
                  })
                }
                style={inputStyle}
              >
                <option value="private">非公開</option>
                <option value="published">公開</option>
              </select>
            </div>

            <div style={modalButtonRowStyle}>
              <button
                onClick={handleCloseSubCourseModal}
                style={cancelButtonStyle}
              >
                キャンセル
              </button>

              <button onClick={handleCreateSubCourse} style={primaryButtonStyle}>
                作成
              </button>
            </div>
          </div>
        </div>
      )}

      {editingCategory && (
        <div style={modalOverlayStyle}>
          <div style={modalCardStyle}>
            <h2 style={modalTitleStyle}>メイン見出し編集</h2>

            <p style={modalSubtitleStyle}>
              見出しタイトル・説明・対象部署を編集します
            </p>

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>見出しタイトル</label>
              <input
                type="text"
                value={editingCategory.title}
                onChange={(e) =>
                  setEditingCategory({
                    ...editingCategory,
                    title: e.target.value,
                  })
                }
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>説明</label>
              <textarea
                value={editingCategory.description || ""}
                onChange={(e) =>
                  setEditingCategory({
                    ...editingCategory,
                    description: e.target.value,
                  })
                }
                style={textareaStyle}
              />
            </div>

            <div style={{ marginBottom: "28px", position: "relative" }}>
              <label style={labelStyle}>対象部署選択（複数選択可）</label>

              <button
                type="button"
                onClick={() =>
                  setIsEditDepartmentDropdownOpen(
                    !isEditDepartmentDropdownOpen
                  )
                }
                style={dropdownButtonStyle}
              >
                <span>{getEditDepartmentDropdownLabel()}</span>
                <span>▼</span>
              </button>

              {isEditDepartmentDropdownOpen && (
                <div style={dropdownPanelStyle}>
                  {departmentOptions.length === 0 && (
                    <div style={emptyTextStyle}>部署がありません</div>
                  )}

                  {departmentOptions.map((department) => (
                    <label key={department} style={checkboxRowStyle}>
                      <input
                        type="checkbox"
                        checked={editSelectedDepartments.includes(department)}
                        onChange={() =>
                          handleEditDepartmentCheckboxChange(department)
                        }
                        style={checkboxStyle}
                      />
                      <span>{department}</span>
                    </label>
                  ))}
                </div>
              )}

              <p style={helperTextStyle}>
                この見出しを表示できる部署を選択してください
              </p>
            </div>

            <div style={modalButtonRowStyle}>
              <button onClick={handleCloseEditModal} style={cancelButtonStyle}>
                キャンセル
              </button>

              <button onClick={handleUpdateCategory} style={primaryButtonStyle}>
                更新
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={pageHeaderStyle}>
        <div>
          <h1 style={pageTitleStyle}>コース管理</h1>

          <p style={pageSubtitleStyle}>
            メイン見出しを作成し、その中にサブコースを管理します
          </p>
        </div>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <button
            onClick={() => navigate("/admin-dashboard")}
            style={cancelButtonStyle}
          >
            ← ダッシュボードへ戻る
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            style={primaryButtonStyle}
          >
            ＋ 新規コース作成
          </button>
        </div>
      </div>

      <div style={categorySectionStyle}>
        <div style={sectionTitleRowStyle}>
          <span style={categoryCountBadgeStyle}>
            {activeCategories.length} 件
          </span>
        </div>

        {activeCategories.length === 0 ? (
          <div style={emptyCategoryBoxStyle}>
            メインカテゴリーがまだありません。右上の「＋ 新規コース作成」から作成してください。
          </div>
        ) : (
          <div style={categoryGridStyle}>
            {activeCategories.map((category) => (
              <div
                key={category.id}
                onClick={() => handleCategoryClick(category)}
                style={categoryCardStyle}
              >
                <div style={categoryImageWrapStyle}>
                  <img
                    src={getThumbnailUrl(category.thumbnail, category)}
                    alt={category.title}
                    style={categoryImageStyle}
                  />

                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenMenuCategoryId(
                        openMenuCategoryId === category.id ? null : category.id
                      );
                    }}
                    style={threeDotButtonStyle}
                  >
                    ⋮
                  </button>

                  {openMenuCategoryId === category.id && (
                    <div
                      style={categoryMenuStyle}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={() => handleCreateSubCourseClick(category)}
                        style={categoryMenuItemStyle}
                      >
                        サブコース作成
                      </button>

                      <button
                        type="button"
                        onClick={() => handleEditCategoryClick(category)}
                        style={categoryMenuItemStyle}
                      >
                        編集
                      </button>



                      <button
  type="button"
  onClick={() => handleDeleteCategory(category.id)}
  style={categoryMenuItemStyle}
>
  削除
</button>




                    </div>
                  )}
                </div>

                <div style={categoryBodyStyle}>
                  <h3 style={categoryTitleStyle}>{category.title}</h3>

                  <p style={categoryDescriptionStyle}>
                    {category.description || "説明が登録されていません"}
                  </p>

                  <div style={departmentLabelStyle}>対象部署</div>

                  <div style={departmentChipWrapStyle}>
                    {category.allowed_departments.length === 0 ? (
                      <span style={departmentChipStyle}>未設定</span>
                    ) : (
                      category.allowed_departments.map((department) => (
                        <span key={department} style={departmentChipStyle}>
                          {department}
                        </span>
                      ))
                    )}
                  </div>

                  <div style={categoryFooterStyle}>
                    <span style={subCourseInfoStyle}>サブコース管理へ</span>
                    <span style={arrowStyle}>→</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



const pageStyle: React.CSSProperties = {
  padding: "40px",
  backgroundColor: "#f3f5f7",
  minHeight: "100vh",
};

const pageHeaderStyle: React.CSSProperties = {
  marginBottom: "24px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
};

const pageTitleStyle: React.CSSProperties = {
  fontSize: "42px",
  fontWeight: 800,
  color: "#07152f",
  margin: 0,
};

const pageSubtitleStyle: React.CSSProperties = {
  marginTop: "8px",
  color: "#64748b",
  fontSize: "22px",
};
const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(15, 23, 42, 0.45)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 999,
};

const modalCardStyle: React.CSSProperties = {
  width: "620px",
  maxHeight: "90vh",
  overflowY: "auto",
  backgroundColor: "#ffffff",
  borderRadius: "24px",
  padding: "32px",
  boxShadow: "0 25px 50px rgba(15, 23, 42, 0.25)",
};

const modalTitleStyle: React.CSSProperties = {
  marginTop: 0,
  marginBottom: "8px",
  fontSize: "28px",
  fontWeight: 800,
  color: "#07152f",
};

const modalSubtitleStyle: React.CSSProperties = {
  marginTop: 0,
  marginBottom: "24px",
  color: "#64748b",
  fontSize: "15px",
  fontWeight: 600,
};

const labelStyle: React.CSSProperties = {
  display: "block",
  marginBottom: "8px",
  fontWeight: 700,
  color: "#07152f",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "14px 16px",
  borderRadius: "14px",
  border: "1px solid #cbd5e1",
  fontSize: "16px",
  outline: "none",
  boxSizing: "border-box",
};

const textareaStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "110px",
  padding: "14px 16px",
  borderRadius: "14px",
  border: "1px solid #cbd5e1",
  fontSize: "16px",
  outline: "none",
  boxSizing: "border-box",
  resize: "vertical",
};

const dropdownButtonStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "52px",
  padding: "14px 16px",
  borderRadius: "14px",
  border: "1px solid #cbd5e1",
  backgroundColor: "#ffffff",
  color: "#07152f",
  fontSize: "16px",
  fontWeight: 600,
  boxSizing: "border-box",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  textAlign: "left",
};

const dropdownPanelStyle: React.CSSProperties = {
  position: "absolute",
  top: "82px",
  left: 0,
  right: 0,
  zIndex: 1000,
  maxHeight: "190px",
  overflowY: "auto",
  padding: "10px 14px",
  borderRadius: "14px",
  border: "1px solid #cbd5e1",
  backgroundColor: "#ffffff",
  boxShadow: "0 18px 35px rgba(15, 23, 42, 0.16)",
  boxSizing: "border-box",
};

const helperTextStyle: React.CSSProperties = {
  marginTop: "8px",
  marginBottom: 0,
  fontSize: "13px",
  color: "#64748b",
};

const cancelButtonStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  backgroundColor: "#ffffff",
  color: "#07152f",
  padding: "12px 18px",
  borderRadius: "14px",
  fontWeight: 700,
  cursor: "pointer",
};

const primaryButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#0f766e",
  color: "#ffffff",
  padding: "12px 18px",
  borderRadius: "14px",
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 10px 24px rgba(15, 118, 110, 0.22)",
};

const checkboxRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "9px 4px",
  color: "#07152f",
  fontSize: "15px",
  fontWeight: 600,
  cursor: "pointer",
};

const checkboxStyle: React.CSSProperties = {
  width: "16px",
  height: "16px",
  cursor: "pointer",
  accentColor: "#0f766e",
};

const emptyTextStyle: React.CSSProperties = {
  color: "#94a3b8",
  fontSize: "14px",
  fontWeight: 600,
  padding: "8px 4px",
};

const fileInputStyle: React.CSSProperties = {
  width: "100%",
  marginTop: "10px",
  padding: "12px",
  borderRadius: "12px",
  border: "1px dashed #5eead4",
  backgroundColor: "#f0fdfa",
  cursor: "pointer",
  boxSizing: "border-box",
};

const uploadedFileTextStyle: React.CSSProperties = {
  marginTop: "8px",
  color: "#0f766e",
  fontSize: "13px",
  fontWeight: 700,
};

const thumbnailPreviewBoxStyle: React.CSSProperties = {
  marginTop: "12px",
};

const thumbnailPreviewStyle: React.CSSProperties = {
  width: "180px",
  height: "110px",
  objectFit: "cover",
  borderRadius: "14px",
  border: "1px solid #e2e8f0",
};

const modalButtonRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
};

const categorySectionStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "24px",
  padding: "28px",
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
  marginBottom: "28px",
};

const sectionTitleRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "flex-start",
  gap: "16px",
  marginBottom: "22px",
};

const categoryCountBadgeStyle: React.CSSProperties = {
  backgroundColor: "#ccfbf1",
  color: "#0f766e",
  padding: "8px 14px",
  borderRadius: "999px",
  fontSize: "14px",
  fontWeight: 800,
};

const categoryGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
  gap: "20px",
};

const categoryCardStyle: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: "20px",
  backgroundColor: "#ffffff",
  overflow: "hidden",
  padding: 0,
  textAlign: "left",
  cursor: "pointer",
  boxShadow: "0 8px 22px rgba(15, 23, 42, 0.05)",
};

const categoryImageWrapStyle: React.CSSProperties = {
  position: "relative",
  height: "180px",
  backgroundColor: "#e2e8f0",
  overflow: "visible",
};

const categoryImageStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};

const categoryBodyStyle: React.CSSProperties = {
  padding: "20px",
};

const categoryTitleStyle: React.CSSProperties = {
  margin: 0,
  color: "#07152f",
  fontSize: "22px",
  fontWeight: 900,
};

const categoryDescriptionStyle: React.CSSProperties = {
  minHeight: "44px",
  marginTop: "10px",
  marginBottom: "18px",
  color: "#64748b",
  fontSize: "15px",
  lineHeight: 1.6,
};

const departmentLabelStyle: React.CSSProperties = {
  color: "#07152f",
  fontSize: "14px",
  fontWeight: 800,
  marginBottom: "8px",
};

const departmentChipWrapStyle: React.CSSProperties = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  marginBottom: "18px",
};

const departmentChipStyle: React.CSSProperties = {
  backgroundColor: "#ccfbf1",
  color: "#0f766e",
  padding: "6px 10px",
  borderRadius: "999px",
  fontSize: "13px",
  fontWeight: 800,
};

const categoryFooterStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderTop: "1px solid #f1f5f9",
  paddingTop: "14px",
};

const subCourseInfoStyle: React.CSSProperties = {
  color: "#0f766e",
  fontSize: "14px",
  fontWeight: 900,
};

const arrowStyle: React.CSSProperties = {
  color: "#0f766e",
  fontSize: "20px",
  fontWeight: 900,
};

const emptyCategoryBoxStyle: React.CSSProperties = {
  padding: "36px 20px",
  borderRadius: "18px",
  backgroundColor: "#f8fafc",
  border: "1px dashed #cbd5e1",
  color: "#64748b",
  fontSize: "15px",
  fontWeight: 700,
  textAlign: "center",
};

const threeDotButtonStyle: React.CSSProperties = {
  position: "absolute",
  top: "12px",
  right: "12px",
  width: "36px",
  height: "36px",
  borderRadius: "999px",
  border: "none",
  backgroundColor: "#ffffff",
  color: "#07152f",
  fontSize: "22px",
  fontWeight: 900,
  cursor: "pointer",
  boxShadow: "0 8px 18px rgba(15, 23, 42, 0.18)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
};

const categoryMenuStyle: React.CSSProperties = {
  position: "absolute",
  top: "54px",
  right: "12px",
  zIndex: 20,
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.18)",
  padding: "8px",
  minWidth: "150px",
};

const categoryMenuItemStyle: React.CSSProperties = {
  width: "100%",
  border: "none",
  backgroundColor: "#ffffff",
  color: "#07152f",
  padding: "10px 12px",
  borderRadius: "10px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer",
  textAlign: "left",
};

export default CoursesPage;